import { Controller } from "stimulus";
import { debounce } from "../../util";
import { hideFormInputError, showFormInputError } from "../../util/gecko_primer";
import { formatCurrency } from "../../util/currency";
import { getCompareCoinsTemplate, getCompareNftsTemplate } from "../../template/search_templates";

export default class extends Controller {
  get MARKET_CAP() {
    return "market_cap";
  }

  get FDV() {
    return "fdv";
  }

  static targets = [
    "aTextInput",
    "bTextInput",
    "assetAName",
    "assetBName",
    "comparisonResult",
    "ratio",
    "assetAPrice",
    "assetAMetricValue",
    "assetBMetricValue",
    "selectedMetric",
    "metricText",
    "assetAImage",
    "assetBImage",
    "assetAMarketCapRankBadge",
    "assetBMarketCapRankBadge",
    "popularComparisonsList"
  ];

  static values = {
    defaultAssets: Array,
    preselectedAAsset: Object,
    preselectedBAsset: Object,
    popularComparisons: Object
  };

  searchCount = 0;

  connect() {
    const params = new URLSearchParams(window.location.search);
    this.searchAsset = debounce(this._searchAsset.bind(this), 300);

    this.inputMapping = {
      a: {
        selectedAsset: this.preselectedAAssetValue,
        inputTarget: this.aTextInputTarget,
        inputError: false
      },
      b: {
        selectedAsset: this.preselectedBAssetValue,
        inputTarget: this.bTextInputTarget,
        inputError: false
      }
    };

    this.defaultAssetsData = this.defaultAssetsValue;
    this.searchedAssetsData = {};
    this.metric = params.get("metric") === this.FDV ? this.FDV : this.MARKET_CAP;

    this.initSelectedMetrics();
    this.loadPopularComparisons();
  }

  getSelectedAsset(searchInput) {
    return this.inputMapping[searchInput].selectedAsset;
  }

  initSelectedMetrics() {
    const selectedMetricText = this.isMetricFDV() ? I18n.t("crypto_compare.fully_diluted_valuation") : I18n.t("crypto_compare.market_cap");
    this.selectedMetricTargets.forEach(target => {
      target.textContent = selectedMetricText;
    });
  }

  selectMetric(e) {
    const selectedMetric = e.currentTarget.dataset.value;

    if (selectedMetric === this.metric) {
      return;
    }

    this.metric = selectedMetric;
    this.initSelectedMetrics();
    this.validateAssets();
    this.loadComparison();
    this.loadPopularComparisons();
    this.appendQueryParams();
  }

  validateAssets() {
    ["a", "b"].forEach(searchInput => this.validateAsset(searchInput));
  }

  loadPopularComparisons() {
    const comparisons = this.popularComparisonsValue;
    const container = this.popularComparisonsListTarget;

    container.innerHTML = "";

    if (this.isMetricFDV()) {
      this.renderPopularComparisonsList(comparisons.fdv, container);

    } else {
      this.renderPopularComparisonsList(comparisons.mcap, container);
    }
  }

  renderPopularComparisonsList(assets, target) {
    assets.forEach((item) => {
      const assetAType = item.asset_a.type;
      const assetBType = item.asset_b.type;

      const queryParams = new URLSearchParams();

      if (assetAType === assetBType) {
        queryParams.append(`${assetAType}s`, `${item.asset_a.slug},${item.asset_b.slug}`);
      } else {
        if (assetAType === "coin") {
          queryParams.append("coins", item.asset_a.slug);
        }
        if (assetAType === "nft") {
          queryParams.append("nfts", item.asset_a.slug);
        }
        if (assetBType === "coin") {
          queryParams.append("coins", item.asset_b.slug);
        }
        if (assetBType === "nft") {
          queryParams.append("nfts", item.asset_b.slug);
        }
      }

      if (this.isMetricFDV()) {
        queryParams.append("metric", "fdv");
      }

      const comparePath = `/${I18n.locale}/compare-cryptocurrencies?${queryParams}`;

      const rowDiv = document.createElement("div");
      rowDiv.className = "tw-flex tw-py-2";

      rowDiv.innerHTML = `
      <a href="${comparePath}" class="tw-relative tw-flex tw-justify-between tw-items-center tw-w-full tw-px-3 tw-text-inherit tw-no-underline tw-mb-2">
        <div class="tw-flex tw-items-center tw-min-w-0 tw-w-[42%]">
          <img src="${item.asset_a.image}" alt="${item.asset_a.name}" class="tw-w-7 tw-h-7 tw-rounded-full tw-mr-2">
          <span class="tw-truncate tw-font-medium tw-text-gray-900 dark:tw-text-moon-50">${item.asset_a.name}</span>
        </div>
  
        <i class="tw-absolute tw-left-1/2 tw--translate-x-1/2 tw-text-lg tw--rotate-90 far fa-arrow-up-arrow-down dark:tw-text-moon-100"></i>
  
        <div class="tw-flex tw-items-center tw-min-w-0 tw-w-[42%] tw-ml-auto">
          <img src="${item.asset_b.image}" alt="${item.asset_b.name}" class="tw-w-7 tw-h-7 tw-rounded-full tw-mr-2">
          <span class="tw-truncate tw-font-medium tw-text-gray-900 dark:tw-text-moon-50">${item.asset_b.name}</span>
        </div>
      </a>
      `;

      target.appendChild(rowDiv);
    });
  }

  validateAsset(searchInput) {
    const asset = this.getSelectedAsset(searchInput);

    if (this.isEmptyObject(asset)) {
      return;
    }

    this.clearError(searchInput);

    if (this.returnAssetType(asset) === "coin") {
      if (this.isFDVAndInfiniteTotalSupply(asset) || this.isMcapAndCircSupplyNotVerified(asset)) {
        this.inputMapping[searchInput].inputError = true;
        this.showError(searchInput);
      }
    } else if (this.returnAssetType(asset) === "nft") {
      if (this.isMetricFDV()) {
        this.inputMapping[searchInput].inputError = true;
        this.showNftError(searchInput, "fdv");
      } else if (!asset.totalSupply || asset.totalSupply <= 0) {
        this.inputMapping[searchInput].inputError = true;
        this.showNftError(searchInput, "supply");
      }
    }
  }

  isEmptyObject(object) {
    return Object.keys(object).length === 0;
  }

  returnAssetType(asset) {
    return asset["assetType"] ? asset["assetType"] : "unknown";
  }

  clearError(searchInput) {
    if (!this.inputMapping[searchInput].inputError) {
      return;
    }

    this.inputMapping[searchInput].inputError = false;
    hideFormInputError(this.inputMapping[searchInput].inputTarget);
  }

  isFDVAndInfiniteTotalSupply(coin) {
    return this.isMetricFDV() && coin.isTotalSupplyInfinite;
  }

  isMcapAndCircSupplyNotVerified(coin) {
    return !this.isMetricFDV() && !coin.isCirculatingSupplyVerified;
  }

  isMetricFDV() {
    return this.metric === this.FDV;
  }

  showError(searchInput) {
    const { inputTarget, selectedAsset: { symbol } } = this.inputMapping[searchInput];
    const errorMessage = this.isMetricFDV() ? I18n.t("crypto_compare.no_total_supply_limit", { symbol: symbol }) : I18n.t("crypto_compare.no_circulating_supply", { symbol: symbol });

    showFormInputError(inputTarget, errorMessage);
  }

  showNftError(searchInput, errorType) {
    const { inputTarget } = this.inputMapping[searchInput];

    const errorMessage = {
      fdv: I18n.t("crypto_compare.no_nft_fdv_comparison"),
      supply: I18n.t("crypto_compare.no_nft_total_supply")
    };

    showFormInputError(inputTarget, errorMessage[errorType]);
  }

  _searchAsset(e) {
    const currentCount = this.searchCount;
    this.searchCount++;

    const searchInput = e.target.dataset.searchInput;
    const searchQuery = e.detail.query;
    const searchedAssets = this.searchedAssetsData[searchQuery];

    if (searchedAssets) {
      this.updateSearchDropdown(currentCount, searchInput, searchedAssets?.coins, searchedAssets?.nfts, e.detail);
      return;
    }

    fetch(`/${I18n.locale}/compare-cryptocurrencies/search?query=${searchQuery}`)
      .then(response => response.json())
      .then(json => {
        this.searchedAssetsData[searchQuery] = json;
        const fetchedCoins = json?.coins || [];
        const fetchedNfts = json?.nfts || [];
        this.updateSearchDropdown(currentCount, searchInput, fetchedCoins, fetchedNfts, e.detail);
      });
  }

  updateSearchDropdown(currentCount, searchInput, searchedCoins, searchedNfts, eventDetail) {
    if (currentCount > this.searchCount) {
      return;
    }

    if (!searchedCoins.length && !searchedNfts.length) {
      return eventDetail?.showDropdownMessage();
    }

    eventDetail.clearDropdownItems();

    searchedCoins.forEach((coin, index) => {
      if (index === 0) {
        eventDetail.addDropdownDivider(I18n.t("crypto_compare.coins"));
      }
      const innerHTML = getCompareCoinsTemplate(coin, searchInput);
      eventDetail.addDropdownItem(`["${coin.slug}", "${coin.assetType}"]`, innerHTML);
    });

    searchedNfts.forEach((nft, index) => {
      if (index === 0) {
        eventDetail.addDropdownDivider(I18n.t("crypto_compare.nfts"));
      }
      const innerHTML = getCompareNftsTemplate(nft, searchInput);
      eventDetail.addDropdownItem(`["${nft.slug}", "${nft.assetType}"]`, innerHTML);
    });

    this.inputMapping[searchInput].inputTarget.dataset.searchQuery = eventDetail.query;
  }

  compareAssetBySlug(slug) {
    return asset => asset.slug === slug;
  }

  compareAssetBySlugAndType(slug, assetType) {
    return asset => asset.slug === slug && asset.assetType === assetType;
  }

  select(e) {
    if (e.currentTarget.value === null) {
      return;
    }

    // In GP Form::SelectComponent app/views/v2/crypto_compare/index.html.erb
    // Example value: "["my-coin-slug", "coin"]", "["my-nft-slug", "nft"]"
    const currentTargetValue = JSON.parse(e.currentTarget.value);
    const selectedAssetSlug = currentTargetValue[0]; // slug
    const selectedAssetType = currentTargetValue[1]; // asset type

    if (selectedAssetSlug === null) {
      return;
    }

    const searchInput = e.currentTarget.getAttribute("data-search-input");
    const forAsset = this.getSelectedAsset(searchInput);

    if (this.sameAssetSelected(selectedAssetSlug, selectedAssetType, forAsset)) {
      return;
    }

    if (this.swappedAssetNotInStaticCombo(selectedAssetSlug, forAsset, searchInput)) {
      return;
    }

    const searchQuery = this.inputMapping[searchInput].inputTarget.dataset.searchQuery;

    const asset = this.defaultAssetsData.find(this.compareAssetBySlugAndType(selectedAssetSlug, selectedAssetType))
      || this.searchedAssetsData[searchQuery]?.coins.find(this.compareAssetBySlugAndType(selectedAssetSlug, selectedAssetType))
      || this.searchedAssetsData[searchQuery]?.nfts.find(this.compareAssetBySlugAndType(selectedAssetSlug, selectedAssetType));
    const contentTarget = e.currentTarget._gp_contentTarget;

    this.setSelectedAsset(asset, contentTarget, searchInput);
    this.validateAsset(searchInput);
    this.loadComparison();
    this.appendQueryParams();
  }

  sameAssetSelected(assetSlugSelected, selectedAssetType, asset) {
    const assetASlug = this.getSelectedAsset("a").slug;
    const assetAType = this.getSelectedAsset("a").assetType;
    const assetBSlug = this.getSelectedAsset("b").slug;
    const assetBType = this.getSelectedAsset("b").assetType;

    return this.compareAssetBySlugAndType(assetSlugSelected, selectedAssetType)(asset) && asset.isSearch && !this.compareAssetBySlugAndType(assetASlug, assetAType)(this.preselectedAAssetValue) && !this.compareAssetBySlug(assetBSlug, assetBType)(this.preselectedBAssetValue);
  }

  swappedAssetNotInStaticCombo(assetSlugSelected, asset, searchInput) {
    const assetToCheck = searchInput === "a" ? this.getSelectedAsset("b") : this.getSelectedAsset("a");
    return asset.isSearch && this.compareAssetBySlug(assetSlugSelected)(assetToCheck);
  }

  setSelectedAsset(asset, contentTarget, searchInput) {
    this.inputMapping[searchInput].selectedAsset = asset;
    const targetInput = this.inputMapping[searchInput].inputTarget;

    if (asset.assetType === "coin") {
      contentTarget.innerHTML = getCompareCoinsTemplate(asset, searchInput, { onInputDisplay: true });
    } else if (asset.assetType === "nft") {
      contentTarget.innerHTML = getCompareNftsTemplate(asset, searchInput, { onInputDisplay: true });
    }
  }

  swapAssets() {
    if (this.isAnySelectedAssetEmpty()) {
      return;
    }

    const AContentTarget = this.inputMapping.a.inputTarget.parentElement.querySelector("[x-ref='content']");
    const BContentTarget = this.inputMapping.b.inputTarget.parentElement.querySelector("[x-ref='content']");

    const tempAsset = this.getSelectedAsset("a");
    this.setSelectedAsset(this.getSelectedAsset("b"), AContentTarget, "a");
    this.setSelectedAsset(tempAsset, BContentTarget, "b");

    this.validateAssets();
    this.loadComparison();
    this.appendQueryParams();
  }

  isAnySelectedAssetEmpty() {
    return Object.values(this.inputMapping).some(searchInput => this.isEmptyObject(searchInput.selectedAsset));
  }

  isAnyInputError() {
    return Object.values(this.inputMapping).some(({ inputError }) => inputError);
  }

  loadComparison() {
    if (this.assetsHaveError()) {
      this.hideComparisonResult();
      return;
    }

    this.showComparisonResult();
  }

  assetsHaveError() {
    return this.isAnyInputError() || this.isAnySelectedAssetEmpty();
  }

  hideComparisonResult() {
    this.comparisonResultTarget.classList.add("tw-hidden");
  }

  showComparisonResult() {
    this.comparisonResultTarget.classList.remove("tw-hidden");

    const selectedAAsset = this.getSelectedAsset("a");
    const assetAName = this.returnAssetType(selectedAAsset) === "coin" ? selectedAAsset.name : selectedAAsset.contractName;
    const bAsset = this.getSelectedAsset("b");
    const assetBName = this.returnAssetType(bAsset) === "coin" ? bAsset.name : bAsset.contractName;

    this.updateTargetsTextContent(this.assetANameTargets, assetAName);
    this.updateTargetsTextContent(this.assetBNameTargets, assetBName);
    this.assetAImageTarget.src = selectedAAsset.image;
    this.assetBImageTarget.src = bAsset.image;
    this.assetAMarketCapRankBadgeTarget.textContent = selectedAAsset.marketCapRank ? `#${selectedAAsset.marketCapRank}` : "-";
    this.assetBMarketCapRankBadgeTarget.textContent = bAsset.marketCapRank ? `#${bAsset.marketCapRank}` : "-";

    this.setAssetMarketCapRankBadgeTooltipCopy(selectedAAsset, this.assetAMarketCapRankBadgeTarget);
    this.setAssetMarketCapRankBadgeTooltipCopy(bAsset, this.assetBMarketCapRankBadgeTarget);

    this.generateComparison();
  }

  setAssetMarketCapRankBadgeTooltipCopy(asset, badgeTarget) {
    if (this.returnAssetType(asset) === "coin") {
      badgeTarget.attributes["data-tooltip"].value = I18n.t("crypto_compare.comparison_result.coin_mcap_badge");
    } else if (this.returnAssetType(asset) === "nft") {
      badgeTarget.attributes["data-tooltip"].value = asset.marketCapRank ? I18n.t("crypto_compare.comparison_result.nft_mcap_badge.present") : I18n.t("crypto_compare.comparison_result.nft_mcap_badge.blank");
    }
  }

  generateComparison() {
    const selectedAAsset = this.getSelectedAsset("a");
    const bAsset = this.getSelectedAsset("b");

    const assetAType = this.returnAssetType(selectedAAsset);
    const assetBType = this.returnAssetType(bAsset);

    if (assetAType === "coin" && assetBType === "coin") {
      const aMetricValue = this.getCoinMetricValue(selectedAAsset);
      const bMetricValue = this.getCoinMetricValue(bAsset);

      const ratio = bMetricValue / aMetricValue;
      const priceOfA = selectedAAsset.price * ratio;

      this.updateMetricText();

      this.assetAMetricValueTarget.textContent = formatCurrency(aMetricValue, "usd");
      this.assetBMetricValueTarget.textContent = formatCurrency(bMetricValue, "usd");
      this.assetAPriceTarget.textContent = formatCurrency(priceOfA, "usd");

      this.setRatioDisplay(ratio);
    } else {
      const aMetricValue = this.getNftCoinCompareMetricValue(selectedAAsset, "a");
      const bMetricValue = this.getNftCoinCompareMetricValue(bAsset, "b");
      const assetAMarketCap = selectedAAsset.marketCap || selectedAAsset.marketCapUsd;

      const currentPriceOfA = selectedAAsset.price || selectedAAsset.floorPriceUsd;

      const comparisonPriceOfA = bMetricValue / aMetricValue;
      const ratio = comparisonPriceOfA / currentPriceOfA;

      this.updateMetricText();

      this.assetAMetricValueTarget.textContent = formatCurrency(assetAMarketCap, "usd");
      this.assetBMetricValueTarget.textContent = formatCurrency(bMetricValue, "usd");

      if (this.returnAssetType(selectedAAsset) === "coin") {
        this.assetAPriceTarget.textContent = formatCurrency(comparisonPriceOfA, "usd");
      } else {
        const comparisonPriceOfANative = comparisonPriceOfA / selectedAAsset.nativeCurrencyPriceUsd;
        const nativeCurrencySymbol = selectedAAsset.nativeCurrencySymbol;
        this.assetAPriceTarget.textContent = formatCurrency(comparisonPriceOfA, "usd") + " / " + formatCurrency(comparisonPriceOfANative, nativeCurrencySymbol);
      }

      this.setRatioDisplay(ratio);
    }
  }

  getCoinMetricValue(asset) {
    return this.isMetricFDV() ? asset.fdv : asset.marketCap;
  }

  getNftCoinCompareMetricValue(asset, order) {
    switch (order) {
      case "a":
        return asset.circulatingSupply || asset.totalSupply;
      case "b":
        return asset.marketCap || asset.marketCapUsd;
    }
  }

  updateTargetsTextContent(targets, textContentValue) {
    targets.forEach(target => {
      target.textContent = textContentValue;
    });
  }

  updateMetricText() {
    const metricText = this.isMetricFDV() ? I18n.t("crypto_compare.fdv") : I18n.t("crypto_compare.mcap");
    this.updateTargetsTextContent(this.metricTextTargets, metricText);
  }

  setRatioDisplay(ratio) {
    this.ratioTarget.textContent = `(${ratio.toFixed(2)}x)`;
    this.setRatioTextColor(ratio);
  }

  setRatioTextColor(ratio) {
    if (ratio >= 1) {
      this.ratioTarget.classList.add("tw-text-primary-500");
      this.ratioTarget.classList.remove("tw-text-red-500");
    } else {
      this.ratioTarget.classList.remove("tw-text-primary-500");
      this.ratioTarget.classList.add("tw-text-red-500");
    }
  }

  appendQueryParams() {
    const currentURL = new URL(window.location.href.split("?")[0]);

    if (!this.assetsHaveError()) {
      const assetTypes = Object.values(this.inputMapping).map(({ selectedAsset: obj }) => this.returnAssetType(obj));
      const selectedAssetSlugs = Object.values(this.inputMapping).map(({ selectedAsset: { slug } }) => slug);

      if (assetTypes[0] === assetTypes[1]) {
        switch (assetTypes[0]) {
          case "coin":
            currentURL.searchParams.append("coins", selectedAssetSlugs.join(","));
            break;
          case "nft":
            currentURL.searchParams.append("nfts", selectedAssetSlugs.join(","));
            break;
        }
      } else {
        if (assetTypes[0] === "coin" && assetTypes[1] === "nft") {
          currentURL.searchParams.append("coins", selectedAssetSlugs[0]);
          currentURL.searchParams.append("nfts", selectedAssetSlugs[1]);
        } else if (assetTypes[0] === "nft" && assetTypes[1] === "coin") {
          currentURL.searchParams.append("nfts", selectedAssetSlugs[0]);
          currentURL.searchParams.append("coins", selectedAssetSlugs[1]);
        }
      }
    }

    if (this.isMetricFDV()) {
      currentURL.searchParams.append("metric", this.metric);
    }

    window.history.replaceState({}, "", decodeURIComponent(currentURL));
  }
}
